import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react"

import {
  clampWindowValue,
  fractionFromClientX,
  isInsideWindow,
  nextWindow,
  type DragMode,
} from "helpers/utils/spanRibbonWindow"
import { getWindowIndexes, needsScrubber, type DragSession } from "./helpers"

interface UseRibbonWindowProps {
  // Spans on the line. The window is measured in these, not in pixels.
  total: number
  // Spans visible at once before anyone drags, and the floor a drag can shrink
  // the window to.
  defaultWindow: number
  minWindow: number
  // The pinned span, so a selection made elsewhere can be scrolled into view.
  // Null when nothing is pinned.
  selectedSpanIndex: number | null
}

interface UseRibbonWindowResult {
  // The window as drawn: clamped to what the line actually has.
  start: number
  size: number
  isFitted: boolean
  showScrubber: boolean
  // The spans the lanes render, in order.
  visibleIndexes: number[]
  // Set for the length of a gesture, for the cursor and to kill text selection.
  dragMode: DragMode | null
  trackRef: RefObject<HTMLDivElement | null>
  beginDrag: (mode: DragMode | "track") => (event: ReactPointerEvent<HTMLElement>) => void
  dragHandlers: {
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void
    onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void
  }
  fitAll: () => void
}

/**
 * The window the ribbon shows and the gesture that moves it.
 *
 * Pointer events throughout: `setPointerCapture` keeps move and up on the
 * element that started the gesture, so no window listeners leak, and
 * `touchAction` stops the browser panning where passive handlers can't. The
 * maths itself is in `helpers/utils/spanRibbonWindow`; what lives here is the
 * state, the session and the frame it lands on.
 */
export const useRibbonWindow = ({
  total,
  defaultWindow,
  minWindow,
  selectedSpanIndex,
}: UseRibbonWindowProps): UseRibbonWindowResult => {
  const [windowStart, setWindowStart] = useState(0)
  const [windowSize, setWindowSize] = useState(defaultWindow)
  // Cursor and text-selection only. Flips twice a gesture, not once per move.
  const [dragMode, setDragMode] = useState<DragMode | null>(null)
  const dragRef = useRef<DragSession | null>(null)
  const trackRef = useRef<HTMLDivElement>(null)

  const size = Math.min(windowSize, total) || total
  const start = Math.min(windowStart, Math.max(0, total - size))
  const isFitted = start === 0 && size >= total
  const showScrubber = needsScrubber(total, defaultWindow)

  const visibleIndexes = useMemo(() => getWindowIndexes(start, size), [start, size])

  // A selection made on the map can land outside the window, so bring it into
  // view — but only when the selection itself changes. Following every window
  // change would yank it back over the span mid-drag.
  const followedSelectionRef = useRef<number | null>(null)
  useEffect(() => {
    if (selectedSpanIndex == null || total === 0) {
      // Same span selected again still counts as a new one.
      followedSelectionRef.current = null
      return
    }
    if (followedSelectionRef.current === selectedSpanIndex) return
    followedSelectionRef.current = selectedSpanIndex
    setWindowStart((current) => {
      const clampedStart = Math.min(current, Math.max(0, total - size))
      if (selectedSpanIndex > clampedStart && selectedSpanIndex <= clampedStart + size) {
        return current
      }
      return clampWindowValue(
        selectedSpanIndex - 1 - Math.floor(size / 2),
        0,
        Math.max(0, total - size),
      )
    })
  }, [selectedSpanIndex, size, total])

  const fitAll = useCallback(() => {
    setWindowStart(0)
    setWindowSize(total)
  }, [total])

  const flushDrag = useCallback(() => {
    const session = dragRef.current
    if (!session) return
    session.rafId = null
    if (session.pendingClientX == null) return
    const frac = fractionFromClientX(session.pendingClientX, session.rect)
    session.pendingClientX = null
    const next = nextWindow(session, frac, total, minWindow)
    setWindowStart(next.start)
    setWindowSize(next.size)
  }, [total, minWindow])

  const beginDrag = useCallback(
    (mode: DragMode | "track") => (event: ReactPointerEvent<HTMLElement>) => {
      if (event.pointerType === "mouse" && event.button !== 0) return
      const track = trackRef.current
      if (!track) return
      // A handle resizes; it must not also move the window.
      if (mode !== "track") event.stopPropagation()
      event.preventDefault()
      const rect = track.getBoundingClientRect()
      const atDown = Math.round(fractionFromClientX(event.clientX, rect) * total)
      // Pressing the window takes hold of it; pressing bare track jumps to there.
      const resolved: DragMode =
        mode === "track" ? (isInsideWindow(atDown, start, size) ? "grab" : "jump") : mode
      // jsdom ships PointerEvent but not pointer capture — optional-call it.
      event.currentTarget.setPointerCapture?.(event.pointerId)
      dragRef.current = {
        mode: resolved,
        rect,
        pointerId: event.pointerId,
        startAtDown: start,
        sizeAtDown: size,
        atDown,
        rafId: null,
        pendingClientX: event.clientX,
      }
      setDragMode(resolved)
      flushDrag()
    },
    [start, size, total, flushDrag],
  )

  const handleDragMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const session = dragRef.current
      if (!session || event.pointerId !== session.pointerId) return
      // One update per frame. The last x within a frame wins.
      session.pendingClientX = event.clientX
      if (session.rafId == null) {
        session.rafId = requestAnimationFrame(flushDrag)
      }
    },
    [flushDrag],
  )

  const handleDragEnd = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const session = dragRef.current
      if (!session || event.pointerId !== session.pointerId) return
      if (session.rafId != null) cancelAnimationFrame(session.rafId)
      // The pointer can lift before the frame we queued, so land that last
      // position here or the window stops a frame short of where you let go.
      flushDrag()
      event.currentTarget.releasePointerCapture?.(event.pointerId)
      dragRef.current = null
      setDragMode(null)
    },
    [flushDrag],
  )

  useEffect(
    () => () => {
      const session = dragRef.current
      if (session?.rafId != null) cancelAnimationFrame(session.rafId)
      dragRef.current = null
    },
    [],
  )

  // One identity for the life of the handlers, or the scrubber's `memo` buys
  // nothing: a fresh object here re-renders it on every hover.
  const dragHandlers = useMemo(
    () => ({
      onPointerMove: handleDragMove,
      onPointerUp: handleDragEnd,
      onPointerCancel: handleDragEnd,
    }),
    [handleDragMove, handleDragEnd],
  )

  return {
    start,
    size,
    isFitted,
    showScrubber,
    visibleIndexes,
    dragMode,
    trackRef,
    beginDrag,
    dragHandlers,
    fitAll,
  }
}

export default useRibbonWindow
